<template>
  <div class="modal-wrapper">
    <div class="modal-backdrop-custom" @click="$emit('close')"></div>

    <div class="modal fade show modal-dialog-overlay" style="display: block;" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content shadow-lg">
          <!-- Modal Header -->
          <div class="modal-header bg-gradient-primary text-white">
            <h5 class="modal-title">
              <i :class="mode === 'add' ? 'mdi mdi-folder-plus' : 'mdi mdi-folder-edit'" class="me-2"></i>
              {{ mode === 'add' ? 'Add New Category' : 'Edit Category' }}
            </h5>
            <button @click="$emit('close')" type="button" class="btn-close btn-close-white"></button>
          </div>

          <!-- Modal Body -->
          <div class="modal-body p-4">
            <form @submit.prevent="saveCategory">
              <div class="row">
                <!-- Category Name -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">Category Name <span class="text-danger">*</span></label>
                  <input
                      v-model="formData.categoryName"
                      type="text"
                      class="form-control"
                      :class="{'is-invalid': errors.categoryName}"
                      placeholder="e.g., SUPER ADMIN, EMPLOYEE"
                      maxlength="100"
                      required
                  />
                  <div v-if="errors.categoryName" class="invalid-feedback">
                    {{ errors.categoryName }}
                  </div>
                  <small class="text-muted">
                    This will be the display name for the category ({{ formData.categoryName.length }}/100)
                  </small>
                </div>

                <!-- User Limit -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">User Limit</label>
                  <input
                      v-model.number="formData.usersLimit"
                      type="number"
                      class="form-control"
                      min="0"
                      placeholder="0 = Unlimited"
                  />
                  <small class="text-muted">Set to 0 for unlimited users</small>
                </div>

                <!-- Status -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">Status <span class="text-danger">*</span></label>
                  <select v-model="formData.status" class="form-select" required>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <small class="text-muted">Inactive categories cannot be assigned</small>
                </div>

                <!-- Data Scope -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">Data Scope <span v-if="mode !== 'edit'" class="text-danger">*</span></label>
                  <select v-model="formData.dataScope" class="form-select" :class="{'is-invalid': errors.dataScope}">
                    <option value="" :disabled="mode !== 'edit'">{{ mode === 'edit' ? 'Not set (legacy rules)' : 'Select data scope' }}</option>
                    <option value="COUNTY">County — all employees</option>
                    <option value="DEPARTMENT">Department — own department only</option>
                    <option value="TEAM">Team — own direct reports only</option>
                    <option value="SELF">Self — own records only</option>
                  </select>
                  <div v-if="errors.dataScope" class="invalid-feedback">{{ errors.dataScope }}</div>
                  <small class="text-muted">Applies to every right given to this profile: users only see records of employees in this scope. Supervisors automatically also see their direct reports.</small>
                </div>

                <!-- Current Users (Edit mode only) -->
                <div v-if="mode === 'edit'" class="col-md-6 mb-3">
                  <label class="form-label">Current Users</label>
                  <div class="input-group">
                    <input
                        :value="category?.currentUsers || 0"
                        type="number"
                        class="form-control"
                        disabled
                    />
                    <span class="input-group-text">
                      {{ formData.usersLimit > 0 ? '/ ' + formData.usersLimit : '(Unlimited)' }}
                    </span>
                  </div>
                  <small class="text-muted">Cannot be modified directly</small>
                </div>

                <!-- Description -->
                <div class="col-md-12 mb-3">
                  <label class="form-label">Description</label>
                  <textarea
                      v-model="formData.description"
                      class="form-control"
                      rows="3"
                      placeholder="Enter category description (optional)"
                  ></textarea>
                  <small class="text-muted">Provide a brief description of this category</small>
                </div>
              </div>

              <!-- Validation Summary -->
              <div v-if="Object.keys(errors).length > 0" class="alert alert-danger">
                <strong>Please fix the following errors:</strong>
                <ul class="mb-0 mt-2">
                  <li v-for="(error, field) in errors" :key="field">{{ error }}</li>
                </ul>
              </div>

              <!-- Preview (Edit mode) -->
              <div v-if="mode === 'edit' && category" class="card bg-light mb-3">
                <div class="card-body">
                  <h6 class="card-title">Category Preview</h6>
                  <div class="row">
                    <div class="col-md-4">
                      <small class="text-muted">Name:</small>
                      <p class="mb-0"><strong>{{ formData.categoryName }}</strong></p>
                    </div>
                    <div class="col-md-4">
                      <small class="text-muted">Status:</small>
                      <p class="mb-0">
                        <span :class="formData.status === 'active' ? 'badge bg-success' : 'badge bg-secondary'">
                          {{ formData.status }}
                        </span>
                      </p>
                    </div>
                    <div class="col-md-4">
                      <small class="text-muted">Limit:</small>
                      <p class="mb-0"><strong>{{ formData.usersLimit || 'Unlimited' }}</strong></p>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer">
            <button @click="$emit('close')" type="button" class="btn btn-light">
              <i class="mdi mdi-close me-1"></i>Cancel
            </button>
            <button @click="saveCategory" type="button" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else :class="mode === 'add' ? 'mdi mdi-plus' : 'mdi mdi-content-save'" class="me-1"></i>
              {{ saving ? 'Saving...' : (mode === 'add' ? 'Create Category' : 'Update Category') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute, toast } from "@/api";

export default {
  name: "AddEditCategoryModal",
  props: {
    category: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      required: true,
      validator: value => ['add', 'edit'].includes(value)
    }
  },
  data() {
    return {
      saving: false,
      formData: {
        categoryName: '',
        usersLimit: 0,
        status: 'active',
        description: '',
        dataScope: ''
      },
      errors: {}
    };
  },
  mounted() {
    if (this.mode === 'edit' && this.category) {
      this.formData = {
        categoryName: this.category.categoryName,
        usersLimit: this.category.usersLimit || 0,
        status: this.category.status || 'active',
        description: this.category.description || '',
        dataScope: this.category.data_scope || ''
      };
    }
  },
  methods: {
    validateForm() {
      this.errors = {};

      if (!this.formData.categoryName || !this.formData.categoryName.trim()) {
        this.errors.categoryName = 'Category name is required';
      }

      if (this.formData.categoryName && this.formData.categoryName.length < 2) {
        this.errors.categoryName = 'Category name must be at least 2 characters';
      }

      if (this.formData.categoryName && this.formData.categoryName.length > 100) {
        this.errors.categoryName = 'Category name cannot exceed 100 characters';
      }

      if (this.formData.usersLimit < 0) {
        this.errors.usersLimit = 'User limit cannot be negative';
      }

      if (this.mode !== 'edit' && !this.formData.dataScope) {
        this.errors.dataScope = 'Choose what users of this profile can see';
      }

      return Object.keys(this.errors).length === 0;
    },

    saveCategory() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;

      const data = new FormData();
      data.append("function", this.mode === 'add' ? "createCategory" : "updateCategory");

      if (this.mode === 'edit') {
        data.append("categoryId", this.category.id);
      }

      data.append("categoryName", this.formData.categoryName.trim());
      data.append("usersLimit", this.formData.usersLimit);
      data.append("status", this.formData.status);
      data.append("description", this.formData.description.trim());
      data.append("dataScope", this.formData.dataScope || "");

      execute(data, authUrl)
          .then((res) => {
            this.saving = false;
            if (res.data.success) {
              toast.success(res.data.message || 'Category saved successfully');
              this.$emit('saved');
              this.$emit('close');
            } else {
              toast.error(res.data.message || 'Failed to save category');
            }
          })
          .catch((e) => {
            this.saving = false;
            toast.error('Error saving category: ' + e.message);
          });
    }
  }
};
</script>

<style scoped>
/* FIXED: Proper z-index stacking */
.modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
}

.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10000;
}

.modal-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10001 !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.modal-dialog {
  pointer-events: auto;
  margin: 0;
}

.modal-content {
  position: relative;
  z-index: 10002;
  background: white;
  border-radius: 0.5rem;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
}
</style>